import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { AlertTriangle, CalendarClock, CheckCircle2, ChevronRight, FileBadge, FileText, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import cacDocumentService from '@services/cacDocumentService';
import { CAC_DOC_TYPE_LABELS, type ExpiringItem } from '@/types/cac';
import { describeDaysToExpire, expiringItemLink } from '@/utils/expiry';
import { formatDateBR } from '@/utils/formatters';

const DAY_OPTIONS = [30, 90, 180, 365];

/** Linha da lista de vencimentos (também usada no painel Hoje). */
export const ExpiringRow: React.FC<{ item: ExpiringItem; compact?: boolean }> = ({ item, compact }) => {
  const expired = item.status === 'expired' || item.days_to_expire < 0;
  return (
    <Link
      to={expiringItemLink(item)}
      className={clsx('flex items-center gap-3 rounded-lg px-2 hover:bg-slate-50 transition-colors', compact ? 'py-1.5' : 'py-2.5')}
    >
      <span className={clsx('p-1.5 rounded-lg flex-shrink-0', expired ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600')}>
        {item.kind === 'cac_document' ? <FileBadge size={16} /> : <FileText size={16} />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 truncate">
          {item.kind === 'cac_document' && item.doc_type ? `${CAC_DOC_TYPE_LABELS[item.doc_type]} · ` : ''}
          {item.title}
        </p>
        <p className="text-small truncate">
          {item.firearm_label ? `${item.firearm_label} · ` : ''}
          {formatDateBR(item.expires_at)}
        </p>
      </div>
      <span className={clsx('text-xs whitespace-nowrap font-medium', expired ? 'text-red-600' : 'text-amber-700')}>{describeDaysToExpire(item.days_to_expire)}</span>
      <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
    </Link>
  );
};

/**
 * Acervo CAC → Vencimentos: linha do tempo consolidada (documentos CAC não renovados + documentos
 * comuns com validade), vencidos e a vencer no horizonte escolhido.
 */
export const ExpiringView: React.FC = () => {
  const [days, setDays] = useState(90);
  const [items, setItems] = useState<ExpiringItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await cacDocumentService.getExpiring(days));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const expired = items.filter((i) => i.status === 'expired' || i.days_to_expire < 0);
  const upcoming = items.filter((i) => !(i.status === 'expired' || i.days_to_expire < 0));

  const group = (title: string, icon: React.ReactNode, list: ExpiringItem[], empty: string) => (
    <section className="card p-3 sm:p-4">
      <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2 mb-2">
        {icon}
        {title}
        <span className="badge-neutral">{list.length}</span>
      </h2>
      {list.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">{empty}</p>
      ) : (
        <div className="divide-y divide-slate-100 -mx-1">
          {list.map((i) => (
            <ExpiringRow key={`${i.kind}-${i.id}`} item={i} />
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <CalendarClock size={20} className="text-slate-500" /> Vencimentos
        </h1>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={clsx('px-3 py-1.5 text-sm transition-colors', days === d ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50')}
              >
                {d} dias
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" icon={<RefreshCw />} onClick={load} disabled={loading}>
            Atualizar
          </Button>
        </div>
      </div>
      <p className="text-small">
        Documentos CAC (CR, CRAF, guias de tráfego, porte) ainda não renovados e documentos comuns com validade, vencidos ou
        vencendo nos próximos {days} dias. Clique para abrir o item e renovar ou atualizar.
      </p>

      {loading && items.length === 0 ? (
        <p className="text-center text-slate-400 py-10">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </p>
      ) : (
        <div className={clsx('grid grid-cols-1 lg:grid-cols-2 gap-4', loading && 'opacity-60')}>
          {group('Vencidos', <AlertTriangle size={16} className="text-red-600" />, expired, 'Nada vencido.')}
          {group(`A vencer em ${days} dias`, <CheckCircle2 size={16} className="text-amber-600" />, upcoming, 'Nenhum vencimento no período.')}
        </div>
      )}

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
